/**
 * Add to Collection Modal
 *
 * Create a new collection and add this item to it, or add this item to an
 * existing collection. Opened from the Item Relationship Inspector (Curatorial
 * axis → Collection row). Follows the same UI pattern as the other relationship
 * modals (CreatePackageModal, AddDependencyModal, etc.).
 */

import { useState, useMemo, useEffect } from 'react';
import { X, Search, Layers2, AlertCircle, CheckCircle, Circle } from 'lucide-react';
import { useCollectionsStore } from '../../../stores/prioritization/collectionsStore';

interface AddToCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded?: (collectionId: string) => void;
  /** Pre-populate with the item this modal was opened from. */
  sourceItemId?: string;
}

export function AddToCollectionModal({
  isOpen,
  onClose,
  onAdded,
  sourceItemId,
}: AddToCollectionModalProps) {
  const allCollections = useCollectionsStore((s) => s.collections);
  const memberships = useCollectionsStore((s) => s.memberships);
  const fetchCollections = useCollectionsStore((s) => s.fetchFromSupabase);
  const createCollection = useCollectionsStore((s) => s.createCollection);
  const addItemToCollection = useCollectionsStore((s) => s.addItemToCollection);

  const [mode, setMode] = useState<'create' | 'add'>('add');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Collections the item already belongs to
  const itemCollectionIds = useMemo(() => {
    if (!sourceItemId) return new Set<string>();
    return new Set(
      memberships.filter((m) => m.itemId === sourceItemId).map((m) => m.collectionId),
    );
  }, [memberships, sourceItemId]);

  const availableCollections = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return allCollections
      .filter((c) => !itemCollectionIds.has(c.id))
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
  }, [allCollections, itemCollectionIds, searchQuery]);

  // Default to "create" when there are no eligible collections
  useEffect(() => {
    if (isOpen && availableCollections.length === 0) setMode('create');
  }, [isOpen, availableCollections.length]);

  useEffect(() => {
    if (isOpen) void fetchCollections();
  }, [isOpen, fetchCollections]);

  useEffect(() => {
    if (!isOpen) return;
    setMode(availableCollections.length > 0 ? 'add' : 'create');
    setNewName('');
    setNewDescription('');
    setSearchQuery('');
    setSelectedCollectionId('');
    setError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async () => {
    setError(null);

    if (mode === 'add') {
      if (!selectedCollectionId) {
        setError('Select a collection to add this item to.');
        return;
      }
      if (!sourceItemId) return;
      setIsSaving(true);
      try {
        await addItemToCollection(selectedCollectionId, sourceItemId);
        onAdded?.(selectedCollectionId);
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add to collection.');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // mode === 'create'
    if (!newName.trim()) {
      setError('Give the collection a name.');
      return;
    }
    if (!sourceItemId) return;
    setIsSaving(true);
    try {
      const collection = await createCollection(newName.trim(), newDescription.trim() || undefined);
      if (!collection) {
        setError('Failed to create collection.');
        return;
      }
      await addItemToCollection(collection.id, sourceItemId);
      onAdded?.(collection.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create collection.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <Layers2 className="h-6 w-6 text-[var(--text-ter)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Add to Collection</h2>
              <p className="text-sm text-[var(--text-ter)]">Curatorial grouping — thematic or organizational</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Mode toggle */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Mode</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('add')}
                disabled={availableCollections.length === 0}
                className={`p-3  border-2 text-left transition-colors ${
                  mode === 'add' ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <div className="font-medium text-sm text-[var(--text-pri)]">Add to existing</div>
                <div className="text-xs text-[var(--text-ter)] mt-0.5">
                  {availableCollections.length > 0
                    ? `${availableCollections.length} available`
                    : 'No collections yet'}
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMode('create')}
                className={`p-3  border-2 text-left transition-colors ${
                  mode === 'create' ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="font-medium text-sm text-[var(--text-pri)]">Create new</div>
                <div className="text-xs text-[var(--text-ter)] mt-0.5">Start a brand-new collection</div>
              </button>
            </div>
          </div>

          {mode === 'add' ? (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Select collection
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search collections…"
                  className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-[var(--slate)]"
                />
              </div>
              <div className="border border-[var(--rule)] max-h-56 overflow-y-auto">
                {availableCollections.length === 0 ? (
                  <div className="p-4 text-center text-[var(--text-ter)] text-sm">No collections found</div>
                ) : (
                  availableCollections.map((c) => {
                    const isSelected = selectedCollectionId === c.id;
                    return (
                      <div
                        key={c.id}
                        className={`flex items-center gap-3 p-3 border-b border-[var(--rule)] last:border-b-0 cursor-pointer hover:bg-[var(--surface)] ${
                          isSelected ? 'bg-[var(--slate-dim)]' : ''
                        }`}
                        onClick={() => setSelectedCollectionId(c.id)}
                      >
                        <span className="flex-shrink-0">
                          {isSelected ? (
                            <CheckCircle className="h-5 w-5 text-[var(--text-ter)]" />
                          ) : (
                            <Circle className="h-5 w-5 text-[var(--text-ter)]" />
                          )}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-[var(--text-pri)] truncate">{c.name}</div>
                          {c.description && (
                            <div className="text-xs text-[var(--text-ter)] truncate">{c.description}</div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Collection name <span className="text-[var(--coral)]">*</span>
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Q3 themes, Accessibility work, North Star"
                  className="w-full px-4 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-[var(--slate)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Description <span className="text-[var(--text-ter)] font-normal">(optional)</span>
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="What items belong in this collection and why?"
                  rows={2}
                  className="w-full px-4 py-2 border border-[var(--rule)] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-[var(--slate)]"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)] flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={
              isSaving ||
              (mode === 'add' ? !selectedCollectionId : !newName.trim())
            }
            className="px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving
              ? 'Saving…'
              : mode === 'add'
              ? 'Add to collection'
              : 'Create & add'}
          </button>
        </div>
      </div>
    </div>
  );
}
